"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts"
import { Calendar } from "antd"

const InfoIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className="inline-block text-gray-400 ml-1 cursor-help hover:text-gray-600 transition-colors"
  >
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1" />
    <text x="8" y="11" textAnchor="middle" fontSize="8" fill="currentColor" className="font-bold">
      i
    </text>
  </svg>
)

const StatRow = ({ label, value, showBar = false }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5 }}
    className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 px-2 md:px-0 transition-colors duration-300"
  >
    <div className="flex items-center gap-2">
      <span className="text-gray-600 text-sm md:text-base font-medium">{label}</span>
      <InfoIcon />
    </div>
    {showBar ? (
      <div className="w-full md:w-32 h-2 bg-gradient-to-r from-green-500 to-red-500 rounded-full" />
    ) : (
      <span className="text-gray-900 text-sm md:text-base font-semibold">{value}</span>
    )}
  </motion.div>
)

const StatColumn = ({ stats }) => (
  <div className="flex flex-col">
    {stats.map((stat, idx) => (
      <StatRow key={idx} {...stat} />
    ))}
  </div>
)

const tradeGrowthData = [
  { date: "Dec 31", value: 15000 },
  { date: "Jan 1", value: 14500 },
  { date: "Jan 5", value: 6500 },
  { date: "Jan 10", value: 15000 },
]

const symbolPerformanceData = [
  { name: "USD30", value: 65 },
  { name: "XAUUSD", value: 35 },
]

const pnlWeekdayData = [
  { day: "Mon", value: 250 },
  { day: "Tue", value: -150 },
  { day: "Wed", value: 400 },
  { day: "Thu", value: -100 },
  { day: "Fri", value: 350 },
  { day: "Sat", value: 200 },
]

const pnlHoursData = [
  { hour: "00:00", value: 120 },
  { hour: "04:00", value: 200 },
  { hour: "08:00", value: 350 },
  { hour: "12:00", value: 450 },
  { hour: "16:00", value: 300 },
  { hour: "20:00", value: 150 },
]

const pnlOrderTypeData = [
  { type: "Limit", value: 450 },
  { type: "Market", value: 320 },
  { type: "Stop", value: 200 },
]

const winLossData = [
  { name: "Win", value: 60 },
  { name: "Loss", value: 40 },
]

const CHART_COLORS = {
  line: "#ef4444",
  symbolPie: ["#6b8cdb", "#f5deb3"],
  winLoss: ["#10b981", "#ef4444"],
}

const DateRangeCalendar = ({ onClose }) => {
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="absolute right-0 top-full mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4"
    >
      <div className="flex gap-4 flex-col md:flex-row">
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Start Date</p>
          <Calendar fullscreen={false} onChange={setStartDate} />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">End Date</p>
          <Calendar fullscreen={false} onChange={setEndDate} />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <button className="flex-1 px-3 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors">
          Apply
        </button>
        <button
          onClick={onClose}
          className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
      </div>
    </motion.div>
  )
}

const SymbolFilter = ({ selectedSymbols, onToggleSymbol }) => {
  const symbols = ["USD30", "XAUUSD", "USDCAD", "EURUSD", "USDJPY"]

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
    >
      <div className="flex items-center gap-2 mb-3">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-gray-400">
          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 5v6M5 8h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          placeholder="Search"
          className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded outline-none focus:border-indigo-500"
        />
      </div>

      <a href="#" className="text-sm text-indigo-600 hover:text-indigo-700 font-semibold">
        Reset All
      </a>

      <div className="space-y-2">
        {symbols.map((symbol) => (
          <label
            key={symbol}
            className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
          >
            <input
              type="checkbox"
              checked={selectedSymbols.includes(symbol)}
              onChange={() => onToggleSymbol(symbol)}
              className="w-4 h-4 border border-gray-300 rounded cursor-pointer accent-indigo-600"
            />
            <span className="text-sm text-gray-700">{symbol}</span>
          </label>
        ))}
      </div>
    </motion.div>
  )
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg"
      >
        <p className="text-sm font-semibold text-gray-900">
          {payload[0].payload.date || payload[0].payload.day || payload[0].payload.hour || payload[0].payload.type}
        </p>
        <p className="text-sm text-indigo-600 font-bold">${payload[0].value}</p>
      </motion.div>
    )
  }
  return null
}

export default function TradingOverview() {
  const [activeTab, setActiveTab] = useState("detailed")
  const [showCalendar, setShowCalendar] = useState(false)
  const [activeChart, setActiveChart] = useState("symbol")
  const [selectedSymbols, setSelectedSymbols] = useState(["USD30", "XAUUSD"])

  const leftColumnStats = [
    { label: "Equity", value: "$0" },
    { label: "Balance", value: "$0" },
    { label: "Profitability", showBar: true },
    { label: "Avg. Winning Trade", value: "$398" },
    { label: "Avg. Losing Trade", value: "-$402.29" },
    { label: "Trades", value: "4" },
  ]

  const middleColumnStats = [
    { label: "Lots", value: "4" },
    { label: "Avg. RRR", value: "0" },
    { label: "Win Rate", value: "50%" },
    { label: "Loss Rate", value: "50%" },
    { label: "Profit Factor", value: "-0.99" },
    { label: "Best Trade", value: "$398.61" },
  ]

  const rightColumnStats = [
    { label: "Worst Trade", value: "-$403.52" },
    { label: "Long Won", value: "7.96%" },
    { label: "Short Won", value: "-8.05%" },
    { label: "Gross Profit", value: "$796" },
    { label: "Gross Loss", value: "-$804.58" },
  ]

  const handleToggleSymbol = (symbol) => {
    setSelectedSymbols((prev) => (prev.includes(symbol) ? prev.filter((s) => s !== symbol) : [...prev, symbol]))
  }

  const tabVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
  }

  const chartVariants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  }

  return (
    <div className="w-full bg-white rounded-lg">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 md:p-6 border-b border-gray-200">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className="text-gray-900">
          <path d="M3 3a2 2 0 012-2h10a2 2 0 012 2v2h2a2 2 0 012 2v10a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2h2V3z" />
        </svg>
        <h2 className="text-lg md:text-xl font-bold text-gray-900">Trading Overview</h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 px-4 md:px-6 pt-4 md:pt-6 border-b border-gray-200">
        <motion.button
          onClick={() => setActiveTab("detailed")}
          className={`pb-3 md:pb-4 px-3 md:px-4 text-sm md:text-base font-semibold transition-colors duration-300 ${
            activeTab === "detailed"
              ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50"
              : "text-gray-600 hover:text-gray-900"
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Detailed Stats
        </motion.button>
        <motion.button
          onClick={() => setActiveTab("analytics")}
          className={`pb-3 md:pb-4 px-3 md:px-4 text-sm md:text-base font-semibold transition-colors duration-300 ${
            activeTab === "analytics"
              ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50"
              : "text-gray-600 hover:text-gray-900"
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Analytics
        </motion.button>
      </div>

      {/* Content */}
      <motion.div
        key={activeTab}
        variants={tabVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.3 }}
        className="p-4 md:p-6"
      >
        {activeTab === "detailed" && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="bg-indigo-50 rounded-lg p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                <StatColumn stats={leftColumnStats} />
                <StatColumn stats={middleColumnStats} />
                <StatColumn stats={rightColumnStats} />
              </div>
            </div>

            {/* Trading History */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className="text-gray-900">
                  <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <h3 className="text-lg md:text-xl font-bold text-gray-900">Trading History</h3>
              </div>

              {/* Table */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="overflow-x-auto rounded-lg border border-gray-200 bg-white"
              >
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">SN</th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Open Time
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Open Price
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Close Time
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Close Price
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Profit
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Lots
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Commission
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Swap
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Net Profit
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Symbol
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">
                        Type
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">De</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="13" className="px-4 py-12 text-center">
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.4 }}
                          className="flex flex-col items-center justify-center gap-2"
                        >
                          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="text-gray-300">
                            <rect x="8" y="8" width="32" height="32" rx="2" stroke="currentColor" strokeWidth="2" />
                            <path
                              d="M24 16v16M20 20l4-4m0 8l-4-4m8 4l-4 4m0-8l4 4"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                          <p className="text-gray-400 text-sm">No data</p>
                        </motion.div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </motion.div>
            </div>
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="space-y-6">
            {/* Trade Growth View */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 space-y-4"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
                <h3 className="text-lg md:text-xl font-bold text-gray-900">Trade Growth View</h3>
                <div className="relative">
                  <motion.button
                    onClick={() => setShowCalendar(!showCalendar)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-3 md:px-4 py-2 bg-indigo-50 text-indigo-600 text-sm font-semibold rounded-lg border border-indigo-200 hover:bg-indigo-100 transition-colors"
                  >
                    Filter By Date
                  </motion.button>
                  {showCalendar && <DateRangeCalendar onClose={() => setShowCalendar(false)} />}
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="w-full h-64 md:h-80"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tradeGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={CHART_COLORS.line}
                      strokeWidth={2}
                      dot={{ fill: "#ef4444", r: 5 }}
                      activeDot={{ r: 7 }}
                      isAnimationActive={true}
                      animationDuration={800}
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </motion.div>
            </motion.div>

            {/* Charts Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 space-y-4"
            >
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg md:text-xl font-bold text-gray-900">Graph</h3>
              </div>

              {/* Chart Type Tabs */}
              <div className="flex flex-wrap gap-2 md:gap-3">
                {[
                  { id: "symbol", label: "Symbol Performance" },
                  { id: "weekday", label: "P&L by Weekday" },
                  { id: "hours", label: "P&L by Hours" },
                  { id: "ordertype", label: "P&L Order Type" },
                  { id: "winloss", label: "Win/Loss Ratio" },
                ].map((tab) => (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveChart(tab.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-3 md:px-4 py-2 text-xs md:text-sm font-semibold rounded-lg transition-all duration-300 ${
                      activeChart === tab.id ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {tab.label}
                  </motion.button>
                ))}
              </div>

              {/* Charts Display */}
              <motion.div
                key={activeChart}
                variants={chartVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
                className="mt-6"
              >
                {activeChart === "symbol" && (
                  <div className="flex flex-col lg:flex-row gap-6 items-center justify-center">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6 }}
                      className="w-full md:w-80 h-80"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={symbolPerformanceData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name }) => name}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            animationDuration={800}
                            animationEasing="ease-out"
                          >
                            {symbolPerformanceData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={CHART_COLORS.symbolPie[index % CHART_COLORS.symbolPie.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="relative w-full md:w-64"
                    >
                      <SymbolFilter selectedSymbols={selectedSymbols} onToggleSymbol={handleToggleSymbol} />
                    </motion.div>
                  </div>
                )}

                {activeChart === "weekday" && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    className="w-full h-80"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pnlWeekdayData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="day" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" fill="#6366f1" animationDuration={800} />
                      </BarChart>
                    </ResponsiveContainer>
                  </motion.div>
                )}

                {activeChart === "hours" && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    className="w-full h-80"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pnlHoursData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="hour" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" fill="#06b6d4" animationDuration={800} />
                      </BarChart>
                    </ResponsiveContainer>
                  </motion.div>
                )}

                {activeChart === "ordertype" && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    className="w-full h-80"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pnlOrderTypeData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="type" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" fill="#ec4899" animationDuration={800} />
                      </BarChart>
                    </ResponsiveContainer>
                  </motion.div>
                )}

                {activeChart === "winloss" && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    className="w-full md:w-80 h-80"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={winLossData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                          animationDuration={800}
                          animationEasing="ease-out"
                        >
                          {winLossData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS.winLoss[index]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  )
}
